import {
  BLINQ_PRIVACY_POLICY,
  BLINQ_TERMS_AND_CONDITIONS,
  BLINQ_TERMS_OF_USE,
} from '@constants/urls';
import type { ReactNode } from 'react';

const ASSETS = '/img/referral-landing';

export interface TrustLogoTile {
  name: string;
  src: string;
}

export const TRUST_LOGO_TILE_SIZE = { width: 322, height: 340 };

const trustLogo = (name: string, file: string): TrustLogoTile => ({
  name,
  src: `${ASSETS}/logos/${file}.avif`,
});

export const TRUST_LOGO_TILES: readonly TrustLogoTile[] = [
  trustLogo('MLB', 'mlb'),
  trustLogo('HubSpot', 'hubspot'),
  trustLogo('Google', 'google'),
  trustLogo('Nike', 'nike'),
  trustLogo('Canva', 'canva'),
  trustLogo('Deloitte', 'deloitte'),
  trustLogo('Gong', 'gong'),
  trustLogo('Cartier', 'cartier'),
  trustLogo('Anthropic', 'anthropic'),
  trustLogo('Goldman Sachs', 'goldman-sachs'),
  trustLogo('Salesforce', 'salesforce'),
  trustLogo('Tesla', 'tesla'),
  trustLogo('Stripe', 'stripe'),
  trustLogo('Philips', 'philips'),
  trustLogo('Stanford University', 'stanford-university'),
  trustLogo('PepsiCo', 'pepsico'),
];

const HOMEPAGE_VIDEOS = 'https://storage.googleapis.com/blinq-frontpage-webflow-video-assets';

export type FeatureMediaTone = 'luckyBreak' | 'pink' | 'neutral' | 'olive' | 'gold';

export interface FeatureQuote {
  text: string;
  attribution: string;
  source: keyof typeof REVIEW_SOURCES;
}

export interface Feature {
  key: string;
  tag: string;
  title: string;
  body: string;
  quote: FeatureQuote;
  video: string;
  mediaTone: FeatureMediaTone;
  explore?: { label: string; href: string };
}

/** The homepage's product cards, in the homepage's order and wording. */
export const FEATURES: readonly Feature[] = [
  {
    key: 'share',
    tag: 'Digital Business Cards',
    title: 'A business card that opens doors',
    body: 'Create a card that’s unmistakably you and share it in seconds: QR, tap, link or wallet. The person you meet saves it instantly, with nothing to download, and can share their details straight back in the flow.',
    quote: {
      text: '“Waaay more responses than I’ve had with paper business cards.”',
      attribution: 'App Store',
      source: 'appStore',
    },
    video: `${HOMEPAGE_VIDEOS}/Homepage%20Launch/BPM125_Web_2026_Home_Share_1440x1440_R03_V02%20(1).mp4`,
    mediaTone: 'luckyBreak',
    explore: {
      label: 'Explore digital business cards',
      href: 'https://blinq.me/solutions/digital-business-card',
    },
  },
  {
    key: 'capture',
    tag: 'Contact Capture',
    title: 'From scan to saved contact in one go',
    body: 'Scan a badge, a paper card, a QR code or a LinkedIn profile, even offline. Blinq saves it to your address book before the moment passes and syncs it to the tools you work in.',
    quote: {
      text: '“The offline mode is a total lifesaver.”',
      attribution: 'John J., G2',
      source: 'g2',
    },
    video: `${HOMEPAGE_VIDEOS}/Homepage%20Launch/BPM125_Web_2026_Home_Capture_1440x1440_R03_V01%20(1).mp4`,
    mediaTone: 'pink',
  },
  {
    key: 'enrichment',
    tag: 'AI Enrichment',
    title: 'Contacts made complete in seconds',
    body: 'Start with a name and company or email. Blinq AI searches 25+ live sources for their photo, title, phone, verified email and LinkedIn, filling in the details while you’re still talking.',
    quote: {
      text: "“I get their info AND it's a conversation starter.”",
      attribution: 'Jennifer G., Capterra',
      source: 'capterra',
    },
    video: `${HOMEPAGE_VIDEOS}/Enrichment/BLQ_Web_2026_Enrichment_Hero_1440x1440_R02_V08.mp4`,
    mediaTone: 'neutral',
    explore: {
      label: 'Explore AI Enrichment',
      href: 'https://blinq.me/solutions/contact-enrichment',
    },
  },
  {
    key: 'notetaker',
    tag: 'AI Notetaker',
    title: 'Keep the context, not just the card',
    body: 'Where you met, when, and what you talked about, saved with the person. Add tags and notes, or let AI Notetaker write them for you, in the room or on a phone call.',
    quote: {
      text: '“A handy feature where I can create notes reminding me of what we talked about.”',
      attribution: 'Irfaan M., G2',
      source: 'g2',
    },
    video: `${HOMEPAGE_VIDEOS}/Homepage%20Launch/BPM125_Web_2026_Home_Notetaker_1440x1440_R03_V01%20(1).mp4`,
    mediaTone: 'olive',
    explore: { label: 'Explore AI Notetaker', href: 'https://blinq.me/solutions/ai-notetaker' },
  },
  {
    key: 'integrations',
    tag: 'Integrations',
    title: 'Follow up wherever you work',
    body: 'The contact and the context land wherever you follow up: CRM, phonebook, email or your AI tools via our MCP. Give your next conversation a head start, with the details from the first.',
    quote: {
      text: '“Perfect tool to keep people in touch!”',
      attribution: 'Diego, Trustpilot',
      source: 'trustpilot',
    },
    video: `${HOMEPAGE_VIDEOS}/Homepage%20Launch/BPM125_Web_2026_Home_Integrations_1440x1440_R03_V01%20(1).mp4`,
    mediaTone: 'gold',
    explore: { label: 'Explore all integrations', href: 'https://blinq.me/integrations' },
  },
];

export const INTRO_IMAGE = { src: `${ASSETS}/intro-lifestyle.jpg`, width: 1800, height: 1350 };

/** Photo behind the testimonials, the same one the homepage uses there. */
export const TESTIMONIALS_BACKGROUND = { src: `${ASSETS}/testimonials-bg.avif` };

export const CTA_IMAGE = { src: `${ASSETS}/contacts-cta.avif`, width: 2112, height: 2046 };

export const REVIEW_STARS_IMAGE = { src: `${ASSETS}/reviews/stars.avif`, width: 223, height: 45 };

export interface ReviewSource {
  label: string;
  icon: string;
}

export const REVIEW_SOURCES = {
  capterra: { label: 'Capterra', icon: `${ASSETS}/reviews/capterra.svg` },
  googlePlay: { label: 'Google Play', icon: `${ASSETS}/reviews/google-play.webp` },
  trustpilot: { label: 'Trustpilot', icon: `${ASSETS}/reviews/trustpilot.png` },
  g2: { label: 'G2', icon: `${ASSETS}/reviews/g2.webp` },
  appStore: { label: 'App Store', icon: `${ASSETS}/reviews/app-store.svg` },
} satisfies Record<string, ReviewSource>;

const REVIEW_LINKS = {
  capterra: 'https://www.capterra.com.au/software/1019128/blinq',
  googlePlay: 'https://play.google.com/store/apps/details?id=com.rabbl.blinq',
  trustpilot: 'https://au.trustpilot.com/review/blinq.me',
  g2: 'https://www.g2.com/categories/event-lead-capture',
  appStore: 'https://apps.apple.com/us/app/blinq-digital-business-card/id1324102258',
} satisfies Record<keyof typeof REVIEW_SOURCES, string>;

export interface Review {
  name: string;
  source: keyof typeof REVIEW_SOURCES;
  href: string;
  body: string;
}

const review = (name: string, source: keyof typeof REVIEW_SOURCES, body: string): Review => ({
  name,
  source,
  href: REVIEW_LINKS[source],
  body,
});

export const REVIEW_ROWS: readonly (readonly Review[])[] = [
  [
    review(
      'Martha C.',
      'capterra',
      "I've used it for two years! Everyone who I introduce Blinq to - at any live event - is thoroughly impressed and immediately downloads the app.",
    ),
    review(
      'Bat-Sheva',
      'googlePlay',
      'I love this app so much. Every time I meet someone at an event, and I give them my Blinq card, they end up downloading it immediately too.',
    ),
    review(
      'Liz Realtor Florida',
      'trustpilot',
      'So convenient! Not only for my business; recently I was in an accident and I was able to share my contact when I needed the most! Great tool! 10 stars',
    ),
    review(
      'Ishan S.',
      'g2',
      'I just share one QR code or link and everything is there... my phone number, email and other details in one place. It makes things easier and saves time, especially when meeting new patients.',
    ),
    review(
      'John J.',
      'g2',
      'Seamless integration and reliable performance. It has never failed me when it counts, and the offline mode is a total lifesaver for sharing your card when service is spotty.',
    ),
    review(
      'Leigha',
      'googlePlay',
      "I absolutely LOVE that I can make myself accessible via all of the channels that are connected to me with just a share! It's an amazing tool!",
    ),
    review(
      'Nabin P.',
      'g2',
      'The CRM/contact sync is seamless, which makes networking efficient and eliminates manual follow-ups.',
    ),
  ],
  [
    review(
      'CoCo_withLove',
      'appStore',
      'Set up 2 business cards for 2 businesses in less than 10 minutes. Definitely recommend to anyone starting a business.',
    ),
    review(
      'KD432',
      'appStore',
      'Every client I’ve gotten has given me compliments on using this app… they’ve also started using the app.',
    ),
    review(
      'SusanVette',
      'appStore',
      'Was skeptical at first but the ease to create and share sold me. Have shared this hundreds of times in personal settings, not just business!',
    ),
    review(
      'Kaeci Carter',
      'googlePlay',
      "Such a game-changer! Wow! I've been told by several top realtors to get Blinq a while ago. Now I wish I would have gotten it much sooner!",
    ),
    review(
      'The Att Boy',
      'appStore',
      'Had it for a week. Waaay more responses than I’ve had with paper business cards. Highly recommend.',
    ),
    review(
      'Keshia W.',
      'g2',
      'Blinq connects seamlessly with my CRM and updates contacts automatically. That’s been a huge time-saver.',
    ),
    review(
      'Sicsean',
      'appStore',
      'You just wouldn’t believe how much this app helps in marketing your brand. It’s right in your hand… so convenient in getting my business out there amongst passersby.',
    ),
  ],
];

export interface Faq {
  question: string;
  answer: ReactNode;
}

const FAQ_LINKS = {
  signup: 'https://dash.blinq.me/signup',
  ios: 'https://dl.blinq.me/?v=web02',
  android: 'https://dl.blinq.me/?v=android01',
  premium: 'https://dash.blinq.me/onboarding/upgrade-flow-selection?plan=premium&interval=year',
  widgets: 'https://blinq.me/solutions/digital-business-card/widgets',
  wallet: 'https://blinq.me/solutions/digital-business-card/apple-and-google-wallet',
  watch: 'https://blinq.me/solutions/digital-business-card/apple-watch',
  digitalBusinessCard: 'https://blinq.me/solutions/digital-business-card',
  business: 'https://blinq.me/business',
  pricing: 'https://blinq.me/pricing',
  nfc: 'https://blinq.me/accessories/nfc-business-cards',
  emailSignature: 'https://blinq.me/solutions/email-signature',
} as const;

const FaqLink = ({ href, children }: { href: string; children: ReactNode }) => (
  <a href={href} target="_blank" rel="noreferrer">
    <strong>{children}</strong>
  </a>
);

export const FAQ_COLUMNS: readonly (readonly Faq[])[] = [
  [
    {
      question: 'How do I create a digital business card?',
      answer: (
        <p>
          Creating your Blinq digital business card takes less than a minute. Start by{' '}
          <FaqLink href={FAQ_LINKS.signup}>creating your card</FaqLink>, or by downloading the app (
          <FaqLink href={FAQ_LINKS.ios}>iOS</FaqLink>/
          <FaqLink href={FAQ_LINKS.android}>Android</FaqLink>). You'll be guided through the card
          creation process, where you can add your contact details, a profile photo, and customize
          your card with your company logo. Once done, you'll be ready to start sharing straight
          away.
        </p>
      ),
    },
    {
      question: 'Is Blinq free to use?',
      answer: (
        <p>
          Yes! Blinq is free for everyone. You’ll get access to digital business cards, virtual
          backgrounds, and dynamic email signatures. With{' '}
          <FaqLink href={FAQ_LINKS.premium}>Blinq Premium</FaqLink>, you unlock added customization
          and export options, which are free to trial for 7 days.
        </p>
      ),
    },
    {
      question: "How do I share my Blinq card with someone who doesn't have the app?",
      answer: (
        <p>
          If the recipient doesn’t have the Blinq app, your card will open in their web browser.
          They can easily save your details and share theirs back with you, making it easy and
          convenient to reconnect.
        </p>
      ),
    },
    {
      question: 'How do I share a digital business card?',
      answer: (
        <p>
          Sharing your digital business card is super simple. You can have recipients scan the QR
          code from your Blinq app, or share it via text, email,{' '}
          <FaqLink href={FAQ_LINKS.widgets}>Widgets</FaqLink>,{' '}
          <FaqLink href={FAQ_LINKS.wallet}>Apple Wallet</FaqLink>, or even from your{' '}
          <FaqLink href={FAQ_LINKS.watch}>Apple Watch</FaqLink>. It’s instant and convenient—perfect
          for sharing in person or online.
        </p>
      ),
    },
    {
      question:
        'What’s the difference between a digital business card and a traditional business card?',
      answer: (
        <p>
          A Blinq digital business card is like a traditional business card, but made for modern
          networking! No more printing costs, cards getting lost, or outdated details. It’s always
          on your phone, ready to share, and can capture the details of the person you’re meeting,
          making it easy to follow up—plus, it’s eco-friendly and completely customizable to match
          your business.
        </p>
      ),
    },
    {
      question: 'What are the benefits of using a digital business card?',
      answer: (
        <p>
          <strong>Share your contact details easily: </strong>A{' '}
          <FaqLink href={FAQ_LINKS.digitalBusinessCard}>digital business card</FaqLink> is the
          quickest and easiest way to share your professional identity.
          <br />
          <br />
          <strong>Your card is always with you:</strong> Whether you prefer to share using the app,
          from your Apple Wallet, your Smart Watch or home screen widgets, it will be with you
          wherever you and your device go, no need to carry paper cards anymore.
          <br />
          <br />
          <strong>Works every time</strong>: Those who receive your card don’t need the Blinq app to
          get your contact details.
          <br />
          <br />
          <strong>Always stay up-to-date: </strong>If you change your contact information on your QR
          code business card, those who have your card will see your updated information.
        </p>
      ),
    },
  ],
  [
    {
      question: 'How do I get Blinq for my team or business?',
      answer: (
        <p>
          Setting up Blinq for your team is easy. With Blinq Business, you can manage multiple
          cards, customize them with your branding, and sync your team's contacts directly with your
          CRM. Check out our <FaqLink href={FAQ_LINKS.business}>Blinq Business</FaqLink>{' '}
          subscription for more details.
        </p>
      ),
    },
    {
      question: 'How much does a digital business card cost?',
      answer: (
        <p>
          You can create your own Blinq digital business card for free! Start by downloading our top
          rated <FaqLink href={FAQ_LINKS.ios}>iOS app</FaqLink> or{' '}
          <FaqLink href={FAQ_LINKS.android}>Android app</FaqLink>, or get started using our{' '}
          <FaqLink href={FAQ_LINKS.signup}>web dashboard</FaqLink>.
          <br />
          <br />
          Our onboarding process will guide you through the setup process including how to customize
          your card with logos, profile images, colours and more. You’ll then be ready to share your
          digital business card in seconds!
          <br />
          <br />
          You can also sign up for <FaqLink href={FAQ_LINKS.pricing}>Blinq Premium</FaqLink> to
          create more cards for yourself or for{' '}
          <FaqLink href={FAQ_LINKS.business}>Blinq Business</FaqLink> if you’re looking to create
          cards for your wider business team. For full pricing information view our{' '}
          <FaqLink href={FAQ_LINKS.pricing}>Pricing Page</FaqLink>.
        </p>
      ),
    },
    {
      question: 'How do NFC business cards work?',
      answer: (
        <p>
          Blinq offers NFC or ‘Near Field Communication’ business cards for you and your team. NFC
          cards have a chip embedded in each card that can be synced with your Blinq digital
          business card.
          <br />
          <br />
          When you want to share your details, simply tap the card on the user's compatible
          smartphone which will bring up a link containing your Blinq card, it really is like magic.
          You can purchase <FaqLink href={FAQ_LINKS.nfc}>NFC cards</FaqLink> via our app or our
          website today. Alternatively, if you already have an NFC card made by another company, you
          can encode it with your Blinq card by encoding your Blinq card link onto it.
        </p>
      ),
    },
    {
      question: 'How does Blinq integrate with my existing tech stack?',
      answer: (
        <p>
          <FaqLink href={FAQ_LINKS.business}>Blinq Business</FaqLink> has several integrations with
          tools your team already uses, like HubSpot, Salesforce, Microsoft Dynamics, and more. An
          example of how this plays out for teams: you can sync your contacts as leads directly into
          your CRM, automatically provision your team's cards using Microsoft Entra, and remotely
          manage dynamic email signatures using Google Workspace.
        </p>
      ),
    },
    {
      question: 'What are dynamic email signatures, and how do I create one with Blinq?',
      answer: (
        <p>
          <FaqLink href={FAQ_LINKS.emailSignature}>Dynamic email signatures</FaqLink> let you extend
          your brand with every email you send. With Blinq, you can create professional, branded
          signatures that match your digital business card in just a few clicks.
        </p>
      ),
    },
    {
      question: 'What are virtual backgrounds, and how can I use them with Blinq?',
      answer: (
        <p>
          Blinq’s virtual backgrounds let you add a professional touch to your video calls with a
          branded QR code embedded directly into your custom background. Simply choose one of our
          backgrounds or upload your own and showcase your brand during virtual meetings.
        </p>
      ),
    },
  ],
];

export interface FooterLink {
  label: string;
  href: string;
}

export interface FooterColumn {
  title: string;
  links: readonly FooterLink[];
}

export const FOOTER_COLUMNS: readonly FooterColumn[] = [
  {
    title: 'Company',
    links: [
      { label: 'Careers', href: 'https://blinq.me/careers' },
      { label: 'Blog', href: 'https://blinq.me/blog' },
      { label: 'Contact us', href: 'https://blinq.me/contact' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'Help Center', href: 'https://support.blinq.me/en' },
      { label: 'Docs', href: 'https://docs.blinq.me/' },
      { label: 'Blinq vs Popl', href: 'https://blinq.me/comparison/blinq-vs-popl' },
      { label: 'Blinq vs HiHello', href: 'https://blinq.me/comparison/blinq-vs-hihello' },
      { label: 'Blinq vs Uniqode', href: 'https://blinq.me/comparison/blinq-vs-uniqode' },
      { label: 'Blinq vs Dot', href: 'https://blinq.me/comparison/blinq-vs-dot' },
    ],
  },
  {
    title: 'Product',
    links: [
      { label: 'Pricing', href: 'https://blinq.me/pricing' },
      { label: 'Digital Business Cards', href: 'https://blinq.me/solutions/digital-business-card' },
      { label: 'Event Lead Capture', href: 'https://blinq.me/solutions/event-lead-capture' },
      { label: 'Contact Enrichment', href: 'https://blinq.me/solutions/contact-enrichment' },
      { label: 'Integrations', href: 'https://blinq.me/integrations' },
      { label: 'Business & Teams', href: 'https://blinq.me/business' },
      { label: 'Event Badges', href: 'https://blinq.me/solutions/event-badge' },
      { label: 'AI Notetaker', href: 'https://blinq.me/solutions/ai-notetaker' },
      { label: 'Accessories', href: 'https://blinq.me/accessories' },
      { label: 'NFC Business Cards', href: 'https://blinq.me/accessories/nfc-business-cards' },
    ],
  },
  {
    title: 'Trust & Legal',
    links: [
      { label: 'Trust Center', href: 'https://trust.blinq.me/' },
      { label: 'Security', href: 'https://blinq.me/security' },
      { label: 'Cookie Policy', href: 'https://blinq.me/legal/cookie-policy' },
      { label: 'Terms & Conditions', href: BLINQ_TERMS_AND_CONDITIONS },
      { label: 'Terms of Use', href: BLINQ_TERMS_OF_USE },
      { label: 'Partner Program', href: 'https://blinq.me/legal/partner-terms-of-service' },
      {
        label: 'Responsible Disclosure',
        href: 'https://blinq.me/security/responsible-disclosure-program',
      },
      { label: 'Privacy Policy', href: BLINQ_PRIVACY_POLICY },
    ],
  },
];

export interface SocialLink {
  label: string;
  href: string;
  icon: string;
}

export const SOCIAL_LINKS: readonly SocialLink[] = [
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/blinq.app/',
    icon: `${ASSETS}/social/instagram.svg`,
  },
  {
    label: 'YouTube',
    href: 'https://www.youtube.com/channel/UCopwKOpWolEHxJONR5ZVjMQ',
    icon: `${ASSETS}/social/youtube.svg`,
  },
  {
    label: 'TikTok',
    href: 'https://www.tiktok.com/@blinq.me',
    icon: `${ASSETS}/social/tiktok.svg`,
  },
  {
    label: 'LinkedIn',
    href: 'https://linkedin.com/company/blinq-me',
    icon: `${ASSETS}/social/linkedin.svg`,
  },
];

export const LOGIN_HREF = '/login';

export const BOOK_A_DEMO_HREF = '/onboarding/contact-sales';

export const FORTUNE_COOKIE_IMAGE = { src: `${ASSETS}/cookie.avif`, width: 80, height: 80 };

export const FORTUNES = [
  'Luck is made not given',
  'Fortune favors the optimist',
  'Doors will open',
  'A good vibe is a good strategy',
] as const;
